import { AssetType } from '../../../../../types/api';

type AssetKey =
  | 'solar'
  | 'energyStorage'
  | 'evChargers'
  | 'roofing'
  | 'hvac'
  | 'ledLighting'
  | 'other';

export type AssetOption = {
  key: AssetKey;
  assetType: AssetType;
};

const OPTIONS: ReadonlyArray<AssetOption> = [
  { key: 'solar', assetType: AssetType.SOLAR },
  { key: 'energyStorage', assetType: AssetType.ENERGY_STORAGE_BESS },
  { key: 'evChargers', assetType: AssetType.EV_CHARGERS },
  { key: 'roofing', assetType: AssetType.ROOFING },
  { key: 'hvac', assetType: AssetType.HVAC },
  { key: 'ledLighting', assetType: AssetType.LED_LIGHTING },
  { key: 'other', assetType: AssetType.OTHER },
] as const;

type Selection = Partial<Record<AssetKey, boolean>>;
type SubmissionSelection = Selection & {
  otherValue?: string;
  otherPrice?: string | number | null;
};

export type AssetPayload = {
  type: string;
  name: string;
  price?: number;
};

/**
 * Return the AssetType for each currently-selected option in the
 * project-assets form state.
 */
const getSelectedTypes = (
  selection: Selection | undefined
): ReadonlyArray<AssetType> => {
  if (!selection) return [];
  return OPTIONS.filter((opt) => selection[opt.key]).map(
    (opt) => opt.assetType
  );
};

const toOptionalPrice = (
  value: string | number | null | undefined
): number | undefined => {
  if (value === '' || value == null) return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

/**
 * Build the POST payload for one Asset row per selected option.
 * For "Other", the user-entered description becomes the Asset.Name;
 * for the rest, Asset.Name mirrors the type value.
 */
const toAssetPayloads = (
  selection: SubmissionSelection | undefined
): ReadonlyArray<AssetPayload> => {
  if (!selection) return [];
  return OPTIONS.filter((opt) => selection[opt.key]).map((opt) => {
    const payload: AssetPayload = {
      type: opt.assetType,
      name:
        opt.assetType === AssetType.OTHER && selection.otherValue
          ? selection.otherValue
          : opt.assetType,
    };
    // Cost only applies to Other; omit when empty/optional.
    if (opt.assetType === AssetType.OTHER) {
      const price = toOptionalPrice(selection.otherPrice);
      if (price !== undefined) payload.price = price;
    }
    return payload;
  });
};

const toTypesString = (types: ReadonlyArray<AssetType>): string =>
  types.join('; ');

export type ProjectAssetsSelection = Record<AssetKey, boolean> & {
  otherValue?: string;
  otherPrice?: number;
};

const emptySelection = (): ProjectAssetsSelection => ({
  solar: false,
  energyStorage: false,
  evChargers: false,
  roofing: false,
  hvac: false,
  ledLighting: false,
  other: false,
  otherValue: '',
  otherPrice: undefined,
});

const fromTypesString = (
  typesString: string | null | undefined
): ProjectAssetsSelection | undefined => {
  if (!typesString?.trim()) return undefined;
  const types = typesString
    .split(/;\s*|,\s*/)
    .map((type) => type.trim())
    .filter(Boolean);
  const selection = emptySelection();
  for (const type of types) {
    const option = OPTIONS.find((opt) => opt.assetType === type);
    if (option) selection[option.key] = true;
  }
  return selection;
};

export const Asset = {
  OPTIONS,
  getSelectedTypes,
  toAssetPayloads,
  toTypesString,
  emptySelection,
  fromTypesString,
};
